// src/components/AppsList.jsx
import { useMemo } from 'react'
import { useDataStore } from '../stores/dataStore'
import ConnectionCard from './ConnectionCard'

// Risk level styles matching ConnectionCard
const RISK_STYLES = {
  normal:   { bg: 'var(--bg-elevated)',       border: 'var(--border-default)',     accent: 'transparent' },
  info:     { bg: 'rgba(77,157,224,0.04)',     border: 'rgba(77,157,224,0.2)',      accent: 'var(--accent-blue)' },
  warning:  { bg: 'rgba(245,166,35,0.05)',     border: 'rgba(245,166,35,0.25)',     accent: 'var(--accent-amber)' },
  critical: { bg: 'rgba(239,68,68,0.04)',      border: 'rgba(239,68,68,0.2)',      accent: 'var(--accent-red)' },
}

export default function AppsList() {
  const connections = useDataStore(s => s.getVisibleConnections())
  const apps = useMemo(() => {
    const byApp = {}
    for (const conn of connections) {
      const k = conn.appName
      if (!byApp[k]) byApp[k] = { appName: k, connections: [], highestRisk: 'normal', investigating: 0 }
      byApp[k].connections.push(conn)
      if (conn.isRetained) byApp[k].investigating += 1
      // Track highest risk level for this app
      const riskLevels = ['normal', 'info', 'warning', 'critical']
      const currentIndex = riskLevels.indexOf(byApp[k].highestRisk)
      const connIndex = riskLevels.indexOf(conn.riskLevel || 'normal')
      if (connIndex > currentIndex) byApp[k].highestRisk = conn.riskLevel || 'normal'
    }
    return Object.values(byApp).sort((a, b) => b.connections.length - a.connections.length)
  }, [connections])
  const searchQuery = useDataStore(s => s.searchQuery).toLowerCase()

  const filtered = useMemo(
    () => apps.filter(app => !searchQuery || app.appName.toLowerCase().includes(searchQuery)),
    [apps, searchQuery]
  )

  if (filtered.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 32 }}>📱</div>
        <div style={{ color: 'var(--text-secondary)', fontSize: 14 }}>No apps using the internet right now</div>
      </div>
    )
  }

  return (
  <div style={{
    height: '100%', overflowY: 'auto',
    padding: '12px 16px 32px',
    display: 'flex', flexDirection: 'column', gap: 6,
  }}>
    {filtered.map((app) => (
      <div key={app.appName}>
        {/* App group label */}
        <div style={{
          fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase',
          color: RISK_STYLES[app.highestRisk].accent || 'var(--text-secondary)', fontFamily: 'var(--font-mono)',
          padding: '0 4px', marginBottom: 4, marginTop: 8,
        }}>
          {app.appName} · {app.connections.length} {app.connections.length === 1 ? 'connection' : 'connections'}
          {app.investigating > 0 && ` · ${app.investigating} investigating`}
        </div>

        {/* Real ConnectionCards for each connection */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {app.connections.map((conn, i) => (
            <ConnectionCard key={conn.id} connection={conn} index={i} />
          ))}
        </div>
      </div>
    ))}
  </div>
)
}
